
import useReportWindow from '../../../hooks/useReportWindow'
import useAssets from '../../../hooks/useAssets'
import useSelectedGroupSatellite from '../../../hooks/useSelectedGroupSatellite'
import { PresimDataType, PresimPaneltype, WindowKeys } from '../../../utils/scenario-types'
import useScenarioResponse from '../../../hooks/useScenarioResponse'
import usePresimApis from '../../../hooks/usePresimApis'
import useNavigation from '../../../hooks/useNavigation'
import { showToastMessage } from '../../../../../../utils/commonFunctions'
import { Fragment, useEffect, useMemo, useState } from 'react'
import GraphReportWindow from './GraphReportWindow'
import { REPORT_WINDOWS } from '../../../utils/constants'
import { ModalModeType, PositionType, WindowSize } from '../../../../../CommonComponents/modals/Modal'

export interface GraphReportWindowType {
    modalId: string
    targetId: string
    icon: React.ReactNode
    title: string
    open: boolean
    pinned: boolean
    loading: boolean
    position: PositionType
    windowSize: WindowSize
    zIndex: number
    graphs: PresimPaneltype[]
    onExport: (tab: string) => void
    onModalChange: (e: React.MouseEvent, mode: ModalModeType) => void
}

const AllPresimReportWindows = () => {

    const { presim } = useAssets()
    const { missionId, scenarioId } = useNavigation()
    const { handleExportTelemetry } = usePresimApis()
    const { scenarioResponse } = useScenarioResponse()
    const { selectedGroupSatellite } = useSelectedGroupSatellite()
    const { windowStore, onReportModalChange } = useReportWindow()

    const [exportLoading, setExportLoading] = useState<string | boolean>(false)

    const presimReport = useMemo(() => windowStore.modals.presimReport, [windowStore.modals.presimReport])
    const adcsWindows = useMemo(() => presimReport.windows.adcs, [presimReport.windows.adcs])
    const powerWindows = useMemo(() => presimReport.windows.power, [presimReport.windows.power])
    const dataflowWindows = useMemo(() => presimReport.windows.dataflow, [presimReport.windows.dataflow])
    const thermalWindows = useMemo(() => presimReport.windows.thermal, [presimReport.windows.thermal])

    const powerIcon = useMemo(() => REPORT_WINDOWS.presimReport.tabs.find(tab => tab.value === 'power')?.icon, [REPORT_WINDOWS])
    const activePower = useMemo(() => powerWindows.open && powerWindows.detached, [powerWindows])
    const pinnedPower = useMemo(() => activePower && powerWindows.pinned, [activePower, powerWindows])

    const adcsIcon = useMemo(() => REPORT_WINDOWS.presimReport.tabs.find(tab => tab.value === 'adcs')?.icon, [REPORT_WINDOWS])
    const activeAdcs = useMemo(() => adcsWindows.open && adcsWindows.detached, [adcsWindows])
    const pinnedAdcs = useMemo(() => activeAdcs && adcsWindows.pinned, [activeAdcs, adcsWindows])

    const dataFlowIcon = useMemo(() => REPORT_WINDOWS.presimReport.tabs.find(tab => tab.value === 'dataflow')?.icon, [REPORT_WINDOWS])
    const activeDataflow = useMemo(() => dataflowWindows.open && dataflowWindows.detached, [dataflowWindows])
    const pinnedDataflow = useMemo(() => activeDataflow && dataflowWindows.pinned, [activeDataflow, dataflowWindows])

    const thermalIcon = useMemo(() => REPORT_WINDOWS.presimReport.tabs.find(tab => tab.value === 'thermal')?.icon, [REPORT_WINDOWS])
    const activeThermal = useMemo(() => thermalWindows.open && thermalWindows.detached, [thermalWindows])
    const pinnedThermal = useMemo(() => activeThermal && thermalWindows.pinned, [activeThermal, thermalWindows])

    const presimData = useMemo(() => presim?.data || {}, [presim?.data])
    const scenarioName = useMemo(() => scenarioResponse?.scenarioName, [scenarioResponse?.scenarioName])
    const selectedGroup = useMemo(() => selectedGroupSatellite?.groupId || null, [selectedGroupSatellite?.groupId])
    const selectedSatellite = useMemo(() => selectedGroupSatellite?.satelliteId || null, [selectedGroupSatellite?.satelliteId])

    const groups = useMemo(() => scenarioResponse?.satelliteGroups || [], [scenarioResponse?.satelliteGroups])
    const satellites = useMemo(() => groups.find(gp => gp.satelliteGroupId === selectedGroup)?.satellites || [], [groups, selectedGroup])
    const selectedSatelliteName = useMemo(() => satellites.find(sat => sat.satelliteId === selectedSatellite)?.satelliteName || '', [satellites, selectedSatellite])

    const selectedPresimData = useMemo(() => selectedSatellite && presimData[selectedSatellite] || {} as PresimDataType[string], [presimData, selectedSatellite])



    useEffect(() => {
        window.addEventListener("blur", function (e) {
            setTimeout(function () {
                window.focus();
            }, 0);
        });
    }, [])

    const exportTelemetry = async (tab: string) => {
        if (!selectedGroup || !selectedSatellite || !scenarioName || !selectedSatelliteName || !missionId || !scenarioId) return
        setExportLoading(tab)
        try {
            const response = await handleExportTelemetry(missionId, scenarioId, selectedGroup, selectedSatellite, selectedSatelliteName, scenarioName)
            const message = response?.message
            showToastMessage('success', message)
        } catch (error: any) {
            showToastMessage('error', error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const onModalClick = (e: React.MouseEvent, mode: ModalModeType, windowKey: WindowKeys) => {
        onReportModalChange(e, 'child', 'presimReport', mode, windowKey)
    }

    return (
        <Fragment>
            <GraphReportWindow
                icon={adcsIcon}
                title='ADCS'
                modalId='adcs'
                targetId='presim__report__button'
                open={activeAdcs}
                pinned={pinnedAdcs}
                loading={exportLoading === 'adcs'}
                windowSize={adcsWindows.windowSize}
                position={adcsWindows.position}
                zIndex={adcsWindows.zIndex}
                graphs={selectedPresimData?.result?.agilityUrl ?? []}
                onExport={exportTelemetry}
                onModalChange={(e: React.MouseEvent, mode: ModalModeType) => { onModalClick(e, mode, 'adcs') }}
            />
            <GraphReportWindow
                icon={powerIcon}
                title='POWER'
                modalId='power'
                targetId='presim__report__button'
                open={activePower}
                pinned={pinnedPower}
                loading={exportLoading === 'power'}
                windowSize={powerWindows.windowSize}
                position={powerWindows.position}
                zIndex={powerWindows.zIndex}
                graphs={selectedPresimData?.result?.powerUrl ?? []}
                onExport={exportTelemetry}
                onModalChange={(e: React.MouseEvent, mode: ModalModeType) => { onModalClick(e, mode, 'power') }}
            />
            <GraphReportWindow
                icon={dataFlowIcon}
                title='DATA FLOW'
                modalId='dataflow'
                targetId='presim__report__button'
                open={activeDataflow}
                pinned={pinnedDataflow}
                loading={exportLoading === 'dataflow'}
                windowSize={dataflowWindows.windowSize}
                position={dataflowWindows.position}
                zIndex={dataflowWindows.zIndex}
                graphs={selectedPresimData?.result?.dataFlowUrl ?? []}
                onExport={exportTelemetry}
                onModalChange={(e: React.MouseEvent, mode: ModalModeType) => { onModalClick(e, mode, 'dataflow') }}
            />
            <GraphReportWindow
                icon={thermalIcon}
                title='THERMAL'
                modalId='thermal'
                targetId='presim__report__button'
                open={activeThermal}
                pinned={pinnedThermal}
                loading={exportLoading === 'thermal'}
                windowSize={thermalWindows.windowSize}
                position={thermalWindows.position}
                zIndex={thermalWindows.zIndex}
                graphs={selectedPresimData?.result?.thermalUrl ?? []}
                onExport={exportTelemetry}
                onModalChange={(e: React.MouseEvent, mode: ModalModeType) => { onModalClick(e, mode, 'thermal') }}
            />
        </Fragment>
    )
}

export default AllPresimReportWindows